import React, { useState, useEffect, useRef } from "react";
import Container from "react-bootstrap/Container";
import {
    Button,
    Snackbar,
    Alert,
    CircularProgress,
    Chip,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Divider,
    Badge,
    Menu,
    MenuItem,
    FormControl,
    InputLabel,
    Select, useTheme
} from "@mui/material";
import { STATUS_CLOSED_ID } from '../../Constants/StatusConstants'
import AxiosInstance from "../../services/AxiosInstance";
import {
    EMAIL_URL,
    EMAILS_BY_TICKET_ID_URL,
    EMAIL_ATTACHMENT_URL,
} from "../../Constants/Constants";
import { USER_EMAIL_ID } from "../../Constants/StorageConstants";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
    AttachFile,
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    Send,
    Save,
    Preview,
    Delete,
    Download,
    Email as EmailIcon,
    Schedule,
    PriorityHigh,
    PriorityMedium,
    PriorityLow,
    MoreVert,
    AccessTime,
    Person
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { isEmpty } from "../../Constants/RolesConstants";
import { ThemeProvider } from "../v2/home/theme/ThemeProvider";
import { Warning } from "@mui/icons-material";

// Enhanced Toolbar Configuration
const CustomToolbar = () => (
    <div id="toolbar" style={{ border: '1px solid #ccc', borderRadius: '4px 4px 0 0' }}>
        <button className="ql-bold" title="Bold">
            <FormatBold />
        </button>
        <button className="ql-italic" title="Italic">
            <FormatItalic />
        </button>
        <button className="ql-underline" title="Underline">
            <FormatUnderlined />
        </button>
        <select className="ql-header" title="Text Size">
            <option value="1">Heading 1</option>
            <option value="2">Heading 2</option>
            <option value="">Normal</option>
        </select>
        <select className="ql-list" title="List">
            <option value="ordered">Numbered List</option>
            <option value="bullet">Bullet List</option>
        </select>
    </div>
);

// Email Template Component
const EmailTemplate = ({ onSelectTemplate }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const templates = [
        { id: 'greeting', name: 'Greeting', content: 'Hello,\n\nThank you for contacting us.' },
        { id: 'update', name: 'Status Update', content: 'Hello,\n\nThis is an update regarding your ticket.' },
        { id: 'resolution', name: 'Resolution', content: 'Hello,\n\nYour issue has been resolved.' }
    ];

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleTemplateSelect = (template) => {
        onSelectTemplate(template.content);
        handleClose();
    };

    return (
        <>
            <Button
                variant="outlined"
                size="small"
                onClick={handleClick}
                startIcon={<EmailIcon />}
                sx={{ mb: 2 }}
            >
                Templates
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                {templates.map((template) => (
                    <MenuItem
                        key={template.id}
                        onClick={() => handleTemplateSelect(template)}
                    >
                        {template.name}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

// File Upload Component
const FileUpload = ({ attachments, onFileUpload, onRemoveFile, disabled }) => {
    const fileInputRef = useRef(null);

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        files.forEach(file => onFileUpload(file));
        // Reset input
        event.target.value = '';
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <Box sx={{ mb: 2 }}>
            <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                style={{ display: "none" }}
                id="file-upload"
                disabled={disabled}
            />
            <label htmlFor="file-upload">
                <Button
                    size="small"
                    component="span"
                    variant="outlined"
                    startIcon={<AttachFile />}
                    disabled={disabled}
                    sx={{ mb: 1 }}
                >
                    Attach Files
                </Button>
            </label>

            {attachments.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Attachments ({attachments.length})
                    </Typography>
                    <List dense>
                        {attachments.map((file, index) => (
                            <ListItem
                                key={index}
                                secondaryAction={
                                    <IconButton
                                        edge="end"
                                        onClick={() => onRemoveFile(index)}
                                        disabled={disabled}
                                    >
                                        <Delete />
                                    </IconButton>
                                }
                            >
                                <ListItemAvatar>
                                    <Avatar sx={{ width: 32, height: 32 }}>
                                        <AttachFile />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={file.name}
                                    secondary={formatFileSize(file.size)}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}
        </Box>
    );
};

// Email History Component
const EmailHistory = ({ emails, onDownload }) => {
    const [expanded, setExpanded] = useState(false);

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 1 }} />
                Email History ({emails.length})
            </Typography>

            {emails.length === 0 ? (
                <Card>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary" align="center">
                            No emails sent yet
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                emails.map((email, index) => (
                    <Accordion
                        key={email.id || index}
                        expanded={expanded === `panel${index}`}
                        onChange={handleAccordionChange(`panel${index}`)}
                        sx={{ mb: 1 }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel${index}-content`}
                            id={`panel${index}-header`}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="subtitle1" component="div">
                                        {email.subject}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatDate(email.createdAt || new Date())}
                                    </Typography>
                                </Box>
                                {email.fileName && (
                                    <Chip
                                        label="Attachment"
                                        size="small"
                                        icon={<AttachFile />}
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <div
                                    dangerouslySetInnerHTML={{ __html: email.body }}
                                    style={{ marginBottom: '16px' }}
                                />
                                {email.fileName && (
                                    <Button
                                        startIcon={<Download />}
                                        onClick={() => onDownload(email.id, email.fileName, email.extension)}
                                        variant="outlined"
                                        size="small"
                                    >
                                        {email.fileName}
                                    </Button>
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))
            )}
        </Box>
    );
};

function Email(props) {
    let { viewTicketId } = useParams();

    // State management
    const [to, setTo] = useState(localStorage.getItem(USER_EMAIL_ID));
    const [cc, setCC] = useState("");
    const [mailSubject, setMailSubject] = useState(`Ticket #${viewTicketId} - Update`);
    const [content, setContent] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [showPreview, setShowPreview] = useState(false);
    const [priority, setPriority] = useState('medium');
    const [draftSaved, setDraftSaved] = useState(false);

    const initialized = useRef(false);
    const autoSaveTimeout = useRef(null);

    // Auto-save draft functionality
    useEffect(() => {
        if (content.trim() || attachments.length > 0) {
            if (autoSaveTimeout.current) {
                clearTimeout(autoSaveTimeout.current);
            }
            autoSaveTimeout.current = setTimeout(() => {
                saveDraft();
            }, 3000);
        }
        return () => {
            if (autoSaveTimeout.current) {
                clearTimeout(autoSaveTimeout.current);
            }
        };
    }, [content, attachments]);

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getEmails();
            loadDraft();
        }
    }, []);

    const saveDraft = () => {
        const draft = {
            to,
            cc,
            subject: mailSubject,
            content,
            attachments: attachments.map(file => ({ name: file.name, size: file.size })),
            timestamp: new Date().toISOString()
        };
        localStorage.setItem(`email_draft_${viewTicketId}`, JSON.stringify(draft));
        setDraftSaved(true);
        setTimeout(() => setDraftSaved(false), 2000);
    };

    const loadDraft = () => {
        const savedDraft = localStorage.getItem(`email_draft_${viewTicketId}`);
        if (savedDraft) {
            try {
                const draft = JSON.parse(savedDraft);
                setTo(draft.to || to);
                setCC(draft.cc || '');
                setMailSubject(draft.subject || mailSubject);
                setContent(draft.content || '');
                // Note: We can't restore actual files, just show the names
                if (draft.attachments) {
                    console.log('Draft had attachments:', draft.attachments);
                }
            } catch (error) {
                console.error('Error loading draft:', error);
            }
        }
    };

    const clearDraft = () => {
        localStorage.removeItem(`email_draft_${viewTicketId}`);
        setContent('');
        setAttachments([]);
        setCC('');
        setMailSubject(`Ticket #${viewTicketId} - Update`);
    };

    const handleFileUpload = (file) => {
        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
            setNotification({
                open: true,
                message: 'File size must be less than 10MB',
                severity: 'error'
            });
            return;
        }

        // Check for duplicate files
        const isDuplicate = attachments.some(att => att.name === file.name);
        if (isDuplicate) {
            setNotification({
                open: true,
                message: 'File already attached',
                severity: 'warning'
            });
            return;
        }

        setAttachments([...attachments, file]);
    };

    const removeFile = (index) => {
        setAttachments(attachments.filter((_, i) => i !== index));
    };

    const handleTemplateSelect = (templateContent) => {
        setContent(templateContent);
    };

    const getEmails = async () => {
        setLoading(true);
        try {
            const response = await AxiosInstance.get(EMAILS_BY_TICKET_ID_URL + viewTicketId);
            setEmails(response.data);
        } catch (error) {
            setNotification({
                open: true,
                message: 'Failed to load email history',
                severity: 'error'
            });
            console.error('Error loading emails:', error);
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        if (!to.trim()) {
            setNotification({
                open: true,
                message: 'Recipient email is required',
                severity: 'error'
            });
            return false;
        }
        if (!mailSubject.trim()) {
            setNotification({
                open: true,
                message: 'Subject is required',
                severity: 'error'
            });
            return false;
        }
        if (!content.trim()) {
            setNotification({
                open: true,
                message: 'Email content is required',
                severity: 'error'
            });
            return false;
        }
        return true;
    };

    const handleSendEmail = async () => {
        if (!validateForm()) return;

        setSending(true);
        try {
            const formData = new FormData();
            formData.append("toMail", to);
            formData.append("ccMail", cc);
            formData.append("subject", mailSubject);
            formData.append("body", content);
            formData.append("priority", priority);
            formData.append("ticketId", viewTicketId);

            // Append all attachments
            attachments.forEach((file, index) => {
                formData.append(`file`, file);
            });

            const response = await AxiosInstance.post(EMAIL_URL, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setEmails([...emails, response.data]);
            clearDraft();
            setNotification({
                open: true,
                message: 'Email sent successfully!',
                severity: 'success'
            });
        } catch (error) {
            setNotification({
                open: true,
                message: 'Failed to send email. Please try again.',
                severity: 'error'
            });
            console.error('Error sending email:', error);
        } finally {
            setSending(false);
        }
    };

    const handleDownload = async (attachId, attachFileName, fileExt) => {
        try {
            const response = await AxiosInstance.get(EMAIL_ATTACHMENT_URL + attachId, {
                responseType: "blob",
            });

            const fileURL = window.URL.createObjectURL(
                new Blob([response.data], { type: fileExt })
            );

            const alink = document.createElement("a");
            alink.href = fileURL;
            alink.setAttribute("download", attachFileName);
            document.body.appendChild(alink);
            alink.click();
            alink.parentNode.removeChild(alink);

            setNotification({
                open: true,
                message: 'File downloaded successfully',
                severity: 'success'
            });
        } catch (error) {
            setNotification({
                open: true,
                message: 'Failed to download file',
                severity: 'error'
            });
            console.error('Error downloading file:', error);
        }
    };

    const handleNotificationClose = () => {
        setNotification({ ...notification, open: false });
    };

    const getPriorityIcon = () => {
        switch (priority) {
            case 'high':
                return <Warning color="error" />;
            case 'medium':
                return <Warning color="warning" />;
            case 'low':
                return <Warning color="success" />;
            default:
                return <Warning color="warning" />;
        }
    };

    const theme = useTheme();
    return (
        <ThemeProvider>
            <Box sx={{
                width: "100%",
                bgcolor: theme.palette.background.default,
                p: 2,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column"
            }}>
                <Snackbar
                    open={notification.open}
                    autoHideDuration={6000}
                    onClose={handleNotificationClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handleNotificationClose}
                        severity={notification.severity}
                        sx={{ width: '100%' }}
                    >
                        {notification.message}
                    </Alert>
                </Snackbar>

                <Grid container spacing={3} columns={16}>
                    {/* Email Composition Panel */}
                    <Grid size={9}>
                        <Card elevation={2} sx={{ bgcolor: 'background.default' }}>
                            <CardContent>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 2
                                }}>
                                    <Typography variant="h6" component="h2">
                                        Compose Email
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {draftSaved && (
                                            <Chip
                                                label="Draft Saved"
                                                size="small"
                                                color="success"
                                                icon={<Save />}
                                            />
                                        )}
                                        <EmailTemplate onSelectTemplate={handleTemplateSelect} />
                                    </Box>
                                </Box>

                                {/* Email Fields */}
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid size={12}>
                                        <TextField
                                            fullWidth
                                            label="To"
                                            value={to}
                                            onChange={(e) => setTo(e.target.value)}
                                            variant="outlined"
                                            size="small"
                                            disabled
                                            helperText="Sender email (read-only)"
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            fullWidth
                                            label="CC"
                                            value={cc}
                                            onChange={(e) => setCC(e.target.value)}
                                            variant="outlined"
                                            size="small"
                                            placeholder="Enter CC recipients (optional)"
                                        />
                                    </Grid>
                                    <Grid size={8}>
                                        <TextField
                                            fullWidth
                                            label="Subject"
                                            value={mailSubject}
                                            onChange={(e) => setMailSubject(e.target.value)}
                                            variant="outlined"
                                            size="small"
                                            placeholder="Enter email subject"
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Priority</InputLabel>
                                            <Select
                                                value={priority}
                                                label="Priority"
                                                onChange={(e) => setPriority(e.target.value)}
                                                startAdornment={getPriorityIcon()}
                                            >
                                                <MenuItem value="low">Low</MenuItem>
                                                <MenuItem value="medium">Medium</MenuItem>
                                                <MenuItem value="high">High</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                {/* File Upload */}
                                <FileUpload
                                    attachments={attachments}
                                    onFileUpload={handleFileUpload}
                                    onRemoveFile={removeFile}
                                    disabled={!isEmpty(props?.componentPermissions) && !props?.componentPermissions?.["canUpdate"]}
                                />

                                {/* Rich Text Editor */}
                                <Paper elevation={1} sx={{ mb: 2 }}>
                                    <CustomToolbar />
                                    <ReactQuill
                                        theme="snow"
                                        value={content}
                                        onChange={setContent}
                                        modules={{
                                            toolbar: {
                                                container: "#toolbar",
                                            },
                                        }}
                                        style={{ height: "200px" }}
                                        placeholder="Write your email content here..."
                                    />
                                </Paper>

                                {/* Action Buttons */}
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setShowPreview(!showPreview)}
                                        startIcon={<Preview />}
                                    >
                                        {showPreview ? 'Hide Preview' : 'Preview'}
                                    </Button>
                                    <Button
                                        disabled={props.statusId === STATUS_CLOSED_ID}
                                        variant="outlined"
                                        onClick={saveDraft}
                                        startIcon={<Save />}
                                    >
                                        Save Draft
                                    </Button>
                                    {isEmpty(props?.componentPermissions) ||
                                        props?.componentPermissions?.["canUpdate"] ? (
                                        <Button
                                            variant="contained"
                                            onClick={handleSendEmail}
                                            disabled={props.statusId === STATUS_CLOSED_ID || sending}
                                            startIcon={sending ? <CircularProgress size={20} /> : <Send />}
                                        >
                                            {sending ? 'Sending...' : 'Send Email'}
                                        </Button>
                                    ) : null}
                                </Box>

                                {/* Email Preview */}
                                {showPreview && (
                                    <Card sx={{ mt: 2, p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 2 }}>
                                            Email Preview
                                        </Typography>
                                        <Box sx={{ mb: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>To:</strong> {to}
                                            </Typography>
                                            {cc && (
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>CC:</strong> {cc}
                                                </Typography>
                                            )}
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Subject:</strong> {mailSubject}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Priority:</strong> {priority.charAt(0).toUpperCase() + priority.slice(1)}
                                            </Typography>
                                        </Box>
                                        <Divider sx={{ my: 1 }} />
                                        <div dangerouslySetInnerHTML={{ __html: content }} />
                                        {attachments.length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Attachments:</strong> {attachments.map(f => f.name).join(', ')}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Card>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Email History Panel */}
                    <Grid size={7}>
                        <Card elevation={2} sx={{ bgcolor: 'background.default' }}>
                            <CardContent>
                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <EmailHistory emails={emails} onDownload={handleDownload} />
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </ThemeProvider>
    );
}

export default Email;
